import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './AdminProgramCategoriesPage.css';

const AdminProgramCategoriesPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const [formData, setFormData] = useState({
    key: '',
    label: '',
    icon: '📋',
    color: '#666666',
    description: '',
    programTypes: '',
    focusAreas: '',
    targetPopulations: '',
    fundingTypes: '',
    order: 0
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/program-categories');
      setCategories(response.data.categories || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories');
      setLoading(false);
    }
  };

  const handleSeedDatabase = async () => {
    if (!window.confirm('This will seed the database with default categories. Continue?')) {
      return;
    }

    try {
      const response = await api.post('/api/program-categories/seed');
      setSuccess(response.data.message);
      fetchCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to seed database');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category.key);
    setFormData({
      key: category.key,
      label: category.label,
      icon: category.icon,
      color: category.color,
      description: category.description || '',
      programTypes: category.programTypes.join('\n'),
      focusAreas: category.focusAreas.join('\n'),
      targetPopulations: (category.targetPopulations || []).join('\n'),
      fundingTypes: (category.fundingTypes || []).join('\n'),
      order: category.order || 0
    });
    setShowAddForm(false);
  };

  const handleAdd = () => {
    setEditingCategory(null);
    setFormData({
      key: '',
      label: '',
      icon: '📋',
      color: '#666666',
      description: '',
      programTypes: '',
      focusAreas: '',
      targetPopulations: '',
      fundingTypes: '',
      order: 0
    });
    setShowAddForm(true);
  };

  const handleCancel = () => {
    setEditingCategory(null);
    setShowAddForm(false);
    setFormData({
      key: '',
      label: '',
      icon: '📋',
      color: '#666666',
      description: '',
      programTypes: '',
      focusAreas: '',
      targetPopulations: '',
      fundingTypes: '',
      order: 0
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const data = {
      ...formData,
      programTypes: formData.programTypes.split('\n').filter(t => t.trim()),
      focusAreas: formData.focusAreas.split('\n').filter(f => f.trim()),
      targetPopulations: formData.targetPopulations.split('\n').filter(t => t.trim()),
      fundingTypes: formData.fundingTypes.split('\n').filter(f => f.trim())
    };

    try {
      if (editingCategory) {
        // Update existing
        await api.put(`/api/program-categories/${editingCategory}`, data);
        setSuccess('Category updated successfully');
      } else {
        // Create new
        await api.post('/api/program-categories', data);
        setSuccess('Category created successfully');
      }

      fetchCategories();
      handleCancel();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save category');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`Are you sure you want to delete the "${key}" category?`)) {
      return;
    }

    try {
      await api.delete(`/api/program-categories/${key}`);
      setSuccess('Category deleted successfully');
      fetchCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete category');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleToggleActive = async (category) => {
    try {
      await api.put(`/api/program-categories/${category.key}`, {
        isActive: !category.isActive
      });
      setSuccess(`Category ${!category.isActive ? 'activated' : 'deactivated'} successfully`);
      fetchCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update category status');
      setTimeout(() => setError(''), 5000);
    }
  };

  return (
    <div className="admin-program-categories-container">
      <div className="page-header">
        <h1>Manage Program Categories</h1>
        <div className="header-actions">
          <button onClick={handleSeedDatabase} className="btn-seed">
            🌱 Seed Database
          </button>
          <button onClick={handleAdd} className="btn-add" disabled={showAddForm || editingCategory}>
            + Add Category
          </button>
          <button onClick={() => navigate('/staff-health-programs')} className="btn-back">
            ← Back to Programs Management
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {(showAddForm || editingCategory) && (
        <div className="category-form-container">
          <h2>{editingCategory ? 'Edit Category' : 'Add New Category'}</h2>
          <form onSubmit={handleSubmit} className="category-form">
            <div className="form-row">
              <div className="form-group">
                <label>Category Key *</label>
                <input
                  type="text"
                  value={formData.key}
                  onChange={(e) => setFormData({ ...formData, key: e.target.value.toLowerCase() })}
                  placeholder="health"
                  required
                  disabled={editingCategory !== null}
                />
              </div>

              <div className="form-group">
                <label>Label *</label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  placeholder="Health Programs"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Icon *</label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="🏥"
                  required
                />
              </div>

              <div className="form-group">
                <label>Color *</label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Order</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  min="0"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this category"
                rows="2"
              />
            </div>

            <div className="form-group">
              <label>Program Types (one per line) *</label>
              <textarea
                value={formData.programTypes}
                onChange={(e) => setFormData({ ...formData, programTypes: e.target.value })}
                placeholder="Medical Services&#10;Mental Health&#10;Dental Services"
                rows="6"
                required
              />
            </div>

            <div className="form-group">
              <label>Focus Areas (one per line)</label>
              <textarea
                value={formData.focusAreas}
                onChange={(e) => setFormData({ ...formData, focusAreas: e.target.value })}
                placeholder="Physical Health&#10;Mental Health&#10;Spiritual Health"
                rows="6"
              />
            </div>

            <div className="form-group">
              <label>Target Populations (one per line)</label>
              <textarea
                value={formData.targetPopulations}
                onChange={(e) => setFormData({ ...formData, targetPopulations: e.target.value })}
                placeholder="All Ages&#10;Children&#10;Youth&#10;Adults&#10;Elders"
                rows="6"
              />
              <small className="form-help">Leave blank to use default values</small>
            </div>

            <div className="form-group">
              <label>Funding Types (one per line)</label>
              <textarea
                value={formData.fundingTypes}
                onChange={(e) => setFormData({ ...formData, fundingTypes: e.target.value })}
                placeholder="Grant&#10;Subsidy&#10;Free Service&#10;Scholarship"
                rows="6"
              />
              <small className="form-help">Leave blank to use default values</small>
            </div>

            <div className="form-actions">
              <button type="button" onClick={handleCancel} className="btn-cancel">
                Cancel
              </button>
              <button type="submit" className="btn-submit">
                {editingCategory ? 'Update Category' : 'Create Category'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading-message">Loading categories...</div>
      ) : (
        <div className="categories-list">
          <h2>Existing Categories ({categories.length})</h2>
          {categories.length === 0 ? (
            <div className="no-categories">
              <p>No categories found. Click "Seed Database" to create default categories.</p>
            </div>
          ) : (
            <div className="categories-grid">
              {categories.map((category) => (
                <div key={category.key} className="category-card">
                  <div className="category-header" style={{ borderLeftColor: category.color }}>
                    <div className="category-title">
                      <span className="category-icon">{category.icon}</span>
                      <h3>{category.label}</h3>
                      <span className={`status-badge ${category.isActive ? 'active' : 'inactive'}`}>
                        {category.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="category-meta">
                      <span className="category-key">{category.key}</span>
                      <span className="category-order">Order: {category.order}</span>
                    </div>
                  </div>

                  {category.description && (
                    <p className="category-description">{category.description}</p>
                  )}

                  <div className="category-details">
                    <div className="detail-section">
                      <strong>Program Types ({category.programTypes.length})</strong>
                      <ul className="compact-list">
                        {category.programTypes.slice(0, 5).map((type, idx) => (
                          <li key={idx}>{type}</li>
                        ))}
                        {category.programTypes.length > 5 && (
                          <li className="more-items">+{category.programTypes.length - 5} more</li>
                        )}
                      </ul>
                    </div>

                    {category.focusAreas.length > 0 && (
                      <div className="detail-section">
                        <strong>Focus Areas ({category.focusAreas.length})</strong>
                        <ul className="compact-list">
                          {category.focusAreas.slice(0, 3).map((area, idx) => (
                            <li key={idx}>{area}</li>
                          ))}
                          {category.focusAreas.length > 3 && (
                            <li className="more-items">+{category.focusAreas.length - 3} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="category-actions">
                    <button onClick={() => handleEdit(category)} className="btn-edit">
                      Edit
                    </button>
                    <button 
                      onClick={() => handleToggleActive(category)} 
                      className={`btn-toggle ${category.isActive ? 'deactivate' : 'activate'}`}
                    >
                      {category.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button onClick={() => handleDelete(category.key)} className="btn-delete">
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminProgramCategoriesPage;
